import React, { FC } from 'react';
import { DocumentsType } from 'v2-types/order';

import { AssociationType } from '../../types';
import { useRequestDocuments } from './request-documents-modal-hooks';
import {
  Box,
  Dialog, DialogActions, DialogContent, DialogTitle, Typography,
  Alert } from '@mui/material';
import { ContainedButton } from 'components/ContainedButton';
import DatePicker from 'components/date-picker/date-picker';
import { Controller, useWatch } from 'react-hook-form';

import { RequestDocumentItem } from './components/request-document-item';
import { useStyles } from './styles';
import SelectField from 'components/select-field';
import { PartyFile } from './types';

type RequestDocumentsModalProps = {
  open: boolean;
  parties: AssociationType[];
  documents: DocumentsType[];
  handleClose: () => void;
};

export const RequestFileUploadModal: FC<RequestDocumentsModalProps> = ({
  open,
  parties,
  documents,
  handleClose,
}) => {
  const {
    filteredParties,
    onSelectPartyId,
    t,
    isRequestDocumentsLoading,
    control,
    getValues,
    onSendRequest,
    isFormValid,
    errors
  } = useRequestDocuments({
    parties,
    documents,
    onClose: handleClose,
  });

  const form = useWatch({ control });
  const files = (form.files ?? []) as PartyFile[];
  const selectedPartyId = form.partyId ?? "";

  const classes = useStyles();
  return (
    <Dialog open={open} fullWidth onClose={handleClose}>
      <DialogTitle style={{ padding: "0px" }}>
        <Typography variant="h5" className={classes.title}>{t('documents:requestFileUploadTitle')}</Typography>
      </DialogTitle>
      <DialogContent style={{ padding: 0 }}>
        <Box className={classes.container} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {errors !== null &&
            <Alert severity="error" classes={{ icon: classes.errorIconContainer }}>
              <Typography variant="subtitle1">{errors}</Typography>
            </Alert>}

          <Box style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <Typography variant="body1">{t('documents:define-dates')}</Typography>
            <Box style={{ display: "flex", gap: "1rem" }}>
              <Controller
                control={control}
                name="notBefore"
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    disableToolbar={false}
                    label={t('orders:startLabel')}
                    {...field}
                  />
                )}
              />

              <Controller
                control={control}
                name="expiresIn"
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    disableToolbar={false}
                    label={t('orders:endLabel')}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>

          <Box style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <Typography variant="body1">{t('documents:selectTheParty')}</Typography>
            <Controller
              control={control}
              name="partyId"
              rules={{ required: true }}
              render={({ field: { value, ...field } }) => (
                <SelectField
                  label={t('orders:selectPartyLabel')}
                  value={value}
                  handleChange={(e) => { onSelectPartyId(e.target.value); }}
                  data={filteredParties}
                  dataKey="_id"
                  dataValue="_id"
                  dataText="name"
                />
              )}
            />
          </Box>

          {files.length > 0 && (
            <Box>
              <Typography variant="body1">{t('documents:selectTheFiles')}</Typography>
              <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                {files.map((file, fileIdx) => (
                  <RequestDocumentItem
                    key={file.fileId + selectedPartyId}
                    file={file}
                    control={control}
                    requestIndex={fileIdx}
                  />
                ))}
              </Box>
            </Box>
          )}

        </Box>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <ContainedButton
          text={t('common:close')}
          onClick={handleClose}
        />
        <ContainedButton
          text={t('documents:sendRequest')}
          isLoading={isRequestDocumentsLoading}
          onClick={onSendRequest}
          disabled={!isFormValid}
        />
      </DialogActions>
    </Dialog>
  );
};
